import html2canvas from 'html2canvas';

export default class QuizResults {
    constructor() {
        this.container = document.getElementById('quiz-results-container');
        if (!this.container) return;

        // Get initial data from PHP
        this.initialData = JSON.parse(this.container.dataset.initial || '{}');
        this.quizId = this.initialData.quizId;

        if (!this.quizId) return;

        this.progressBar = null;
        this.progressInterval = null;
        this.totalDuration = 180000; // 3 minutes in milliseconds
        this.startTime = null;
        this.hasShownLoader = false;

        this.init();
        this.setupDownloadButton();
    }

    init() {
        // Check for initial error
        if (this.initialData.error) {
            this.handleError(this.initialData.error);
            return;
        }

        // If we have cached results, we don't need to do anything
        // as the results are already displayed by PHP
        if (this.initialData.hasCachedResult) {
            return;
        }

        // Only start checking status if we should
        if (this.initialData.shouldCheckStatus) {
            this.checkStatus();
        }
    }

    checkStatus() {
        // Make the status check request
        fetch(`/wp-admin/admin-ajax.php?action=check_quiz_status&job_id=${this.quizId}`)
            .then(response => response.json())
            .then(result => {
                if (result.success) {
                    this.handleStatusResponse(result.data);
                } else {
                    this.handleError(result.data);
                }
            })
            .catch(error => {
                this.handleError({
                    message: 'Failed to check status. Please try again.',
                    code: 'network_error',
                    details: error.message
                });
            });
    }

    handleStatusResponse(data) {
        // If result is from database, refresh the page to show results
        if (data.source === 'database') {
            window.location.reload();
            return;
        }

        // Handle different statuses
        switch (data.status) {
            case 'IN_QUEUE':
            case 'IN_PROGRESS':
                // Show loader if not already showing
                this.showLoader();

                // Check again after 15 seconds
                setTimeout(() => {
                    this.checkStatus();
                }, 15000); // 15 seconds in milliseconds
                break;

            case 'COMPLETED':
                // Hide loader and refresh
                this.hideLoader();
                // Reload the page to show results from database
                window.location.reload();
                break;

            case 'FAILED':
                // Hide loader and show error
                this.hideLoader();
                this.handleError({
                    message: 'Failed to process your results. Please try again.',
                    code: 'processing_failed'
                });
                break;

            default:
                // Hide loader and show error
                this.hideLoader();
                this.handleError({
                    message: 'Unknown status received',
                    code: 'unknown_status'
                });
        }
    }

    showLoader() {
        // Only show the loader once
        if (this.hasShownLoader) return;

        // Show the loader if it exists
        const loader = document.getElementById('quiz-loader');
        if (loader) {
            loader.style.display = 'block';
            this.progressBar = loader.querySelector('.progress-bar');
            this.startProgress();
            this.hasShownLoader = true;
        }
    }

    startProgress() {
        if (!this.progressBar) return;

        this.startTime = Date.now();
        let resetCount = 0;

        // Clear any existing interval
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
        }

        // Update progress every 100ms
        this.progressInterval = setInterval(() => {
            const elapsed = Date.now() - this.startTime;
            let progress = Math.min((elapsed / this.totalDuration) * 100, 100);

            // If progress reaches 90%, set it back to 80% and increment reset count
            if (progress >= 90 && resetCount < 2) {
                progress = 80;
                resetCount++;
                this.startTime = Date.now() - (this.totalDuration * 0.8); // Adjust start time to maintain smooth progress
            }

            this.progressBar.style.width = `${progress}%`;

            // Only clear the interval if we've reached 100% and haven't reset
            if (progress >= 100) {
                clearInterval(this.progressInterval);
            }
        }, 100);
    }

    hideLoader() {
        const loader = document.getElementById('quiz-loader');
        if (loader) {
            loader.style.display = 'none';
            if (this.progressInterval) {
                clearInterval(this.progressInterval);
            }
        }
    }

    handleError(error) {
        // Clear any existing content
        this.container.innerHTML = '';

        // Create error message with source identification
        const errorMessage = document.createElement('div');
        errorMessage.id = 'error-message';

        // Determine error source and message
        let errorSource = '';
        let errorDetails = '';

        switch (error.code) {
            case 'wordpress_error':
                errorSource = 'WordPress Error';
                errorDetails = error.details;
                break;
            case 'runpod_error':
                errorSource = 'RunPod API Error';
                errorDetails = `Status Code: ${error.details.status_code}`;
                break;
            case 'network_error':
                errorSource = 'Network Error';
                errorDetails = error.details;
                break;
            default:
                errorSource = 'Unknown Error';
                errorDetails = error.message;
        }

        // Only show detailed error in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        errorMessage.innerHTML = `
            <h2>Refresh the page to see your results</h2>
            ${isDevelopment ? `
                <div id="error-details">
                    <p><strong>Error Source:</strong> ${errorSource}</p>
                    <p><strong>Details:</strong> ${errorDetails}</p>
                </div>
            ` : ''}
            <a href="/quiz" class="button">Didn't get your results by email? Take Quiz Again</a>
        `;

        // Add error message to container
        this.container.appendChild(errorMessage);
    }

    setupDownloadButton() {
        const downloadButton = document.getElementById('guide-content-download');
        if (downloadButton) {
            downloadButton.addEventListener('click', () => this.downloadAsPDF());
        }
    }

    downloadAsPDF() {
        // Add print-specific styles temporarily
        const style = document.createElement('style');
        style.textContent = `
            @media print {
                body * {
                    visibility: hidden;
                }
                #results-content, #results-content * {
                    visibility: visible;
                }
                #guide-feedback {
                    visibility: hidden;
                }
                #results-content {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                }
                .guide-content__container {
                    page-break-inside: avoid;
                }
                .card {
                    page-break-inside: avoid;
                }
                .card-grid{
                    display: flex;
                    flex-direction: column;
                }
                    
            }
        `;
        document.head.appendChild(style);

        // Trigger print dialog
        window.print();

        // Remove the print styles after printing
        document.head.removeChild(style);
    }
} 